import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { useEffect, useMemo } from 'react'
import { getDefaultPath, getRedirectPath } from 'services/auth'
import { auth_useRedirectPath$key } from '__generated__/auth_useRedirectPath.graphql'
import { auth_useDefaultPath$key } from '__generated__/auth_useDefaultPath.graphql'
import { useRouter } from 'next/router'
import { auth_useAuthGate$key } from '__generated__/auth_useAuthGate.graphql'
import { Nullable } from 'types'

/**
 * Get the homepage / landing page path for the given user.
 */
export function useDefaultPath(userFragmentRef: auth_useDefaultPath$key) {
  const user = useFragment(graphql`
    fragment auth_useDefaultPath on User {
      ...auth_getDefaultPath
    }
  `, userFragmentRef)

  return useMemo(() => getDefaultPath(user), [user])
}

/**
 * Get where the user should be redirected to, if they can't access the current URL, depending on what the
 * current URL is.
 */
export function useRedirectPath(userFragmentRef: Nullable<auth_useRedirectPath$key>) {
  const router = useRouter()
  const user = useFragment(graphql`
    fragment auth_useRedirectPath on User {
      ...auth_getRedirectPath
    }
  `, userFragmentRef)

  return useMemo(() => getRedirectPath(user, router?.asPath), [user, router?.asPath])
}

interface AuthGateOptions {
  /**
   * Optionally override the redirect location
   */
  redirectTo?: string
}

export function useAuthGate(userFragmentRef: Nullable<auth_useAuthGate$key>, allowed: boolean, options: AuthGateOptions = {}) {
  const router = useRouter()
  const user = useFragment(graphql`
    fragment auth_useAuthGate on User {
      ...auth_useRedirectPath
    }
  `, userFragmentRef)

  const defaultRedirectPath = useRedirectPath(user)
  const redirectPath = useMemo(() => (
    options?.redirectTo || defaultRedirectPath
  ), [options?.redirectTo, defaultRedirectPath])

  useEffect(() => {
    if (!allowed && redirectPath) {
      // TODO: implement redirect loop check
      console.log('AuthGate redirecting to', redirectPath)
      router.push(redirectPath)
    }
  }, [redirectPath, allowed])

  return useMemo(() => allowed ? null : redirectPath, [allowed, redirectPath])
}
